import { supabase } from '@/lib/supabase'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import {
  fetchChatPostAttachmentsByChatIds,
  type ChatPostAttachment,
} from './chatPostAttachmentsService'

export type ChatItem = {
  id: number
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
  title: string
  contentHtml: string
  attachments: ChatPostAttachment[]
  createdAt: string
}

export async function fetchChatEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_chat_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_chat_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_chat_enabled error', e)
    return false
  }
}

export async function fetchChatPostEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_chat_post_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_chat_post_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_chat_post_enabled error', e)
    return false
  }
}

export async function createChat(params: {
  title: string
  html: string
}): Promise<ChatItem | null> {
  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return null

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuario nao autenticado')

  const meta = (user?.user_metadata || {}) as any

  let profileName: string | null = null
  let profileAvatarUrl: string | null | undefined

  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles' as any)
      .select('full_name,avatar_url')
      .eq('id', userId)
      .maybeSingle()

    if (!profileError && profile) {
      profileName = (profile as any).full_name ?? null
      if (
        typeof (profile as any).avatar_url === 'string' ||
        (profile as any).avatar_url === null
      ) {
        profileAvatarUrl = (profile as any).avatar_url as string | null
      }
    }
  } catch {
    // fallback para metadata
  }

  const rawName =
    (profileName && String(profileName).trim().length > 0 ? profileName : null) ??
    (meta.full_name as string | null | undefined) ??
    (meta.name as string | null | undefined) ??
    (user?.email as string | null | undefined) ??
    null

  const authorName = normalizeAuthorName(rawName)

  let authorAvatarUrl: string | null = null
  if (profileAvatarUrl !== undefined) {
    authorAvatarUrl = profileAvatarUrl
  } else if (typeof meta.avatar_url === 'string' || meta.avatar_url === null) {
    authorAvatarUrl = meta.avatar_url as string | null
  } else {
    authorAvatarUrl = null
  }

  const { data, error } = await supabase
    .from('market_chats' as any)
    .insert({
      author_id: userId,
      author_name: authorName,
      author_avatar_url: authorAvatarUrl,
      title,
      content_html: html,
    } as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('createChat error', error)
    throw error
  }

  const [item] = await mapRowsToChatItems([data as any])
  return item ?? null
}

export async function listChats(params: {
  page: number
  pageSize?: number
}): Promise<{ items: ChatItem[] }> {
  const page = Math.max(1, Number(params.page) || 1)
  const pageSize = Math.max(1, Number(params.pageSize) || 25)
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data, error } = await supabase
    .from('market_chats' as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,created_at')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listChats error', error)
    throw error
  }

  const rows = Array.isArray(data) ? data : []
  const items = await mapRowsToChatItems(rows)

  const ids = items.map((it) => it.id)
  if (ids.length > 0) {
    const attachmentsMap = await fetchChatPostAttachmentsByChatIds(ids)
    for (const item of items) {
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return { items }
}

export async function updateChat(
  id: number,
  params: {
    title: string
    html: string
  },
): Promise<void> {
  const chatId = Number(id)
  if (!Number.isFinite(chatId) || chatId <= 0) return

  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return

  const payload: any = {
    title,
    content_html: html,
  }

  const { error } = await supabase
    .from('market_chats' as any)
    .update(payload)
    .eq('id', chatId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('updateChat error', error)
    throw error
  }
}

export async function deleteChat(id: number): Promise<void> {
  const chatId = Number(id)
  if (!Number.isFinite(chatId) || chatId <= 0) return

  const { error } = await supabase
    .from('market_chats' as any)
    .delete()
    .eq('id', chatId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteChat error', error)
    throw error
  }
}

async function mapRowsToChatItems(rowsRaw: any): Promise<ChatItem[]> {
  const rows = Array.isArray(rowsRaw) ? rowsRaw : []
  if (!rows.length) return []

  const authorIds = Array.from(
    new Set(
      rows
        .map((row: any) => (row.author_id ? String(row.author_id) : ''))
        .filter((id: string) => !!id),
    ),
  )

  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (authorIds.length > 0) {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles' as any)
        .select('id,full_name,avatar_url')
        .in('id', authorIds as any)

      if (profilesError) {
        // eslint-disable-next-line no-console
        console.error('listChats profiles error', profilesError)
      } else if (Array.isArray(profiles)) {
        profiles.forEach((p: any) => {
          const id = p.id ? String(p.id) : ''
          if (!id) return
          profilesMap[id] = {
            name: String(p.full_name || '').trim(),
            avatarUrl:
              typeof p.avatar_url === 'string' || p.avatar_url === null
                ? (p.avatar_url as string | null)
                : null,
          }
        })
      }
    } catch {
      // fallback para snapshots
    }
  }

  const items: ChatItem[] = rows.map((row: any) => {
    const authorId = row.author_id ? String(row.author_id) : ''
    const profileOverride = authorId ? profilesMap[authorId] : undefined

    const overrideName = profileOverride?.name?.trim()
    const rawName =
      (overrideName && overrideName.length > 0 ? overrideName : null) ??
      (typeof row.author_name === 'string' ? (row.author_name as string) : null)

    const authorName = normalizeAuthorName(rawName)

    let authorAvatarUrl: string | null = null
    if (profileOverride && profileOverride.avatarUrl !== undefined) {
      authorAvatarUrl = profileOverride.avatarUrl
    } else if (
      typeof row.author_avatar_url === 'string' ||
      row.author_avatar_url === null
    ) {
      authorAvatarUrl = row.author_avatar_url as string | null
    }

    return {
      id: Number(row.id),
      authorId,
      authorName,
      authorAvatarUrl,
      title: String(row.title || '').trim(),
      contentHtml: String(row.content_html || ''),
      attachments: [],
      createdAt: row.created_at ? String(row.created_at) : '',
    }
  })

  return items
}
